// Tradução das falhas da API de pagamento (ASAAS) para o formando.
//
// A API devolve o motivo real da recusa (CPF inválido, cartão negado, CEP
// inexistente...), mas o modal descartava a resposta e a tela mostrava sempre
// "Tente novamente mais tarde", sem permitir identificar o que corrigir.
// Estas funções extraem o motivo e montam uma frase que aponta o dado errado.

// Rótulo do campo por código de erro do ASAAS, usado quando a descrição da API
// não deixa claro de qual dado ela está falando.
const CAMPO_POR_CODIGO = {
  invalid_cpfCnpj: 'CPF/CNPJ do titular',
  invalid_creditCard: 'Dados do cartão',
  invalid_credit_card: 'Dados do cartão',
  invalid_creditCardHolderInfo: 'Dados do titular do cartão',
  invalid_creditCardNumber: 'Número do cartão',
  invalid_creditCardHolderName: 'Nome impresso no cartão',
  invalid_postalCode: 'CEP do titular',
  invalid_address: 'Endereço do titular',
  invalid_addressNumber: 'Número da residência',
  invalid_phone: 'Celular do titular',
  invalid_mobilePhone: 'Celular do titular',
  invalid_email: 'E-mail do titular',
  invalid_name: 'Nome do titular',
  invalid_value: 'Valor do pedido',
  invalid_dueDate: 'Data de vencimento',
  invalid_installment: 'Parcelamento',
  invalid_installmentCount: 'Número de parcelas',
  invalid_billingType: 'Forma de pagamento',
  invalid_customer: 'Cadastro do pagador na operadora',
  invalid_action: 'Operação não permitida para este pedido',
};

// Quando a API não manda descrição, a mensagem vem do status HTTP.
const MENSAGEM_POR_STATUS = {
  400: 'A operadora recusou os dados enviados. Confira CPF/CNPJ, nome, e-mail, celular e CEP do titular antes de tentar de novo.',
  401: 'Sua sessão expirou. Saia e entre novamente no sistema para concluir o pagamento.',
  403: 'Você não tem permissão para finalizar este pedido. Fale com a comissão ou com a agência.',
  404: 'O pedido ou o contrato não foi encontrado no servidor de pagamento. Recarregue a página e tente de novo.',
  409: 'Já existe um pedido em andamento para este formando. Confira em "Meus pedidos" antes de tentar de novo.',
  422: 'A operadora recusou o pagamento com os dados informados. Confira os dados do titular e do cartão.',
  429: 'Muitas tentativas seguidas de pagamento. Aguarde alguns minutos e tente novamente.',
  502: 'O servidor de pagamento está indisponível no momento. Tente novamente em alguns minutos.',
  503: 'O servidor de pagamento está indisponível no momento. Tente novamente em alguns minutos.',
  504: 'O servidor de pagamento demorou demais para responder. Tente novamente em alguns minutos.',
};

const MENSAGEM_PADRAO =
  'Não foi possível finalizar a compra do seu pedido. Tente novamente mais tarde!';

function texto(valor) {
  if (!valor) return '';
  if (typeof valor === 'string') return valor.trim();

  return String(
    valor.description || valor.message || valor.error || valor.msg || ''
  ).trim();
}

// Devolve [{ code, description }] com os erros que a API informou, olhando os
// formatos já vistos na resposta: { errors }, { responseError: { errors } },
// { error: "..." } e { message: "..." }.
export function extrairErrosPagamento(erro) {
  // Em erro de rede o axios não traz `response`; nesse caso o próprio objeto
  // só tem `message` ("Network Error"), que não é um erro vindo da operadora.
  const ehErroAxios = !!(
    erro &&
    (erro.isAxiosError || erro.response || erro.request || erro.config)
  );
  const dados =
    (ehErroAxios ? erro.response && erro.response.data : erro) || {};

  const listas = [
    dados.errors,
    dados.responseError && dados.responseError.errors,
    dados.error && dados.error.errors,
    dados.data && dados.data.errors,
  ];

  for (const lista of listas) {
    if (Array.isArray(lista) && lista.length > 0) {
      const erros = lista
        .map((item) => ({
          code: (item && item.code) || '',
          description: texto(item),
        }))
        .filter((item) => item.code || item.description);

      if (erros.length > 0) return erros;
    }
  }

  const simples = texto(dados.error) || texto(dados.message) || texto(dados.msg);

  return simples ? [{ code: '', description: simples }] : [];
}

// Frase exibida ao formando: diz qual dado foi recusado sempre que a API
// informa, e só cai no texto genérico quando não há nenhuma pista.
export function mensagemErroPagamento(erro, mensagemPadrao = MENSAGEM_PADRAO) {
  const erros = extrairErrosPagamento(erro);

  if (erros.length > 0) {
    const partes = erros.map(({ code, description }) => {
      const campo = CAMPO_POR_CODIGO[code];

      if (!description) return campo || code;
      if (!campo) return description;

      // Evita "CPF/CNPJ do titular: O CPF informado é inválido" virar
      // redundante quando a própria descrição já cita o campo.
      const jaCita = description.toLowerCase().includes(campo.toLowerCase());

      return jaCita ? description : `${campo}: ${description}`;
    });

    return partes.filter(Boolean).join(' | ');
  }

  const status = erro && erro.response && erro.response.status;

  if (status) {
    return (
      MENSAGEM_POR_STATUS[status] ||
      (status >= 500
        ? `O servidor de pagamento falhou ao processar o pedido (erro ${status}). Tente novamente em alguns minutos.`
        : `A operadora recusou o pagamento (erro ${status}). Confira os dados informados e tente novamente.`)
    );
  }

  if (erro && erro.code === 'ECONNABORTED') {
    return 'O servidor de pagamento demorou demais para responder. Confira em "Meus pedidos" se a compra foi registrada antes de tentar de novo.';
  }

  if (erro && erro.request) {
    return 'Não conseguimos falar com o servidor de pagamento. Verifique sua conexão com a internet e tente novamente.';
  }

  return texto(erro) || mensagemPadrao;
}

// Texto técnico completo para o console e para o log do Telegram, com o que
// era perdido antes: status, endereço chamado e corpo devolvido pela API.
export function detalhesErroPagamento(erro) {
  const resposta = (erro && erro.response) || {};
  const config = (erro && erro.config) || {};

  const linhas = [
    `mensagem: ${(erro && erro.message) || 'sem mensagem'}`,
    `status: ${resposta.status || 'sem resposta'}`,
    `url: ${config.method ? String(config.method).toUpperCase() + ' ' : ''}${config.url || 'desconhecida'}`,
  ];

  const codigos = extrairErrosPagamento(erro)
    .map(({ code, description }) => [code, description].filter(Boolean).join(' - '))
    .filter(Boolean);

  if (codigos.length > 0) linhas.push(`erros: ${codigos.join(' | ')}`);

  if (resposta.data !== undefined) {
    let corpo;

    try {
      corpo = typeof resposta.data === 'string' ? resposta.data : JSON.stringify(resposta.data);
    } catch (e) {
      corpo = '[corpo não serializável]';
    }

    linhas.push(`corpo: ${String(corpo).substring(0, 1000)}`);
  }

  return linhas.join('\n');
}
